<template>
  <div class="ui-gp">
    <div class="ui-gp__head">
      <span class="ui-gp__game">
        <i class="fa fa-gamepad"></i> {{ gameTitle }}
      </span>
      <span v-if="!game.id" class="tf-badge tf-badge--warn">Не сохранён</span>
      <span v-else class="ui-gp__id">#{{ game.id }}</span>
      <div class="ui-gp__spacer"></div>
      <button type="button" class="tf-btn-primary" :disabled="isSaving || !isFilled" @click.prevent="saveData">
        <i class="fa" :class="isSaving ? 'fa-spinner tf-spin' : 'fa-check'"></i>
        {{ isSaving ? 'Сохраняем…' : 'Сохранить' }}
      </button>
      <button v-if="game.id" type="button" class="tf-btn-ghost ui-gp__wipe"
              :disabled="isWiping" @click.prevent="confirmOverwrite">
        <i class="fa" :class="isWiping ? 'fa-spinner tf-spin' : 'fa-eraser'"></i>
        {{ isWiping ? 'Затираем…' : 'Затереть' }}
      </button>
    </div>

    <div class="ui-gp__body">
      <div class="ui-gp__field">
        <label class="tf-label" :for="'gp-nick-' + fieldKey">Ник</label>
        <b-form-input :id="'gp-nick-' + fieldKey" v-model="gameProfileNick"
                      type="text" placeholder="Ник в игре" @keyup.enter="saveData"/>
      </div>
      <div class="ui-gp__field">
        <label class="tf-label" :for="'gp-id-' + fieldKey">{{ idLabel }}</label>
        <b-form-input :id="'gp-id-' + fieldKey" v-model="gameProfileId"
                      type="number" placeholder="Игровой ID" @keyup.enter="saveData"/>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api/api";
import {UsersAdapterMixin} from "@/mixins/Users/UsersAdapterMixin";

const ID_FIELDS = {"pubg-mobile": "pubg_id", mlbb: "mlbb_id"};
const ID_LABELS = {"pubg-mobile": "PUBG ID", mlbb: "MLBB ID"};

export default {
  name: "GameProfiles",
  mixins: [UsersAdapterMixin],
  props: {
    game: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      fieldsIds: ID_FIELDS,
      gameNames: {"pubg-mobile": "slug", mlbb: "slug"},
      isSaving: false,
      isWiping: false,
    };
  },
  computed: {
    gameSlug() {
      return (this.game && this.game.game && this.game.game.slug) || '';
    },

    gameTitle() {
      return (this.game && this.game.game && this.game.game.name) || 'Дисциплина';
    },

    idLabel() {
      return ID_LABELS[this.gameSlug] || 'Игровой ID';
    },

    /** id профиля для уникальных id полей: у только что добавленного профиля его ещё нет */
    fieldKey() {
      return this.game && (this.game.id || this.gameSlug || 'new');
    },

    isFilled() {
      return Boolean(String(this.gameProfileNick || '').trim()) && Boolean(String(this.gameProfileId || '').trim());
    },
  },
  methods: {
    async createGameProfile() {
      try {
        await api.users.createGameProfile({
          game_id: this.game.game_id,
          user_id: this.game.user_id,
          fields: {
            nick: this.gameProfileNick,
            ...(this.gameSlug === 'pubg-mobile' && {pubg_id: this.gameProfileId}),
            ...(this.gameSlug === 'mlbb' && {mlbb_id: this.gameProfileId}),
          }
        });
        this.$toast.success('Игровой профиль успешно создан!');
        return true;
      } catch (error) {
        this.$toast.error('Ошибка при создании игрового профиля');
        return false;
      }
    },

    async updateGameProfile() {
      const userProfile = {
        fields: {
          nick: this.gameProfileNick,
          ...(this.gameName.includes("pubg-mobile") && {
            pubg_id: this.gameProfileId,
          }),
          ...(this.gameName.includes("mlbb") && {
            mlbb_id: this.gameProfileId,
          }),
        },
      };

      try {
        await api.users.updateUserFields(this.game.id, userProfile);
        this.$toast.success('Игровой профиль сохранён');
        return true;
      } catch (error) {
        // раньше ошибка уходила только в консоль и страница всё равно перезагружалась
        this.$toast.error('Не удалось сохранить игровой профиль');
        return false;
      }
    },

    async saveData() {
      if (this.isSaving || !this.isFilled) return;
      this.isSaving = true;
      try {
        const saved = await this[this.game.id ? "updateGameProfile" : "createGameProfile"]();
        if (saved) this.$emit("submit");
      } finally {
        this.isSaving = false;
      }
    },

    /** Затирание необратимо — раньше срабатывало с первого клика. */
    async confirmOverwrite() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
          `Затереть ник и игровой ID профиля ${this.gameTitle}?`, {
            title: 'Затирание данных',
            okTitle: 'Затереть',
            cancelTitle: 'Отмена',
            okVariant: 'danger',
            centered: true,
          });
      if (!confirmed) return;

      this.isWiping = true;
      try {
        await this.overwriteFields({userId: this.game.id});
        await this.showUser(this.$route.params.id);
        this.$emit("submit");
      } finally {
        this.isWiping = false;
      }
    },
  },
  created() {
    this.gameProfileNick = this.$props.game.fields.nick;
    this.gameProfileId =
        this.$props.game.fields[this.fieldsIds[this.$props.game.game.slug]];
    this.gameName =
        this.$props.game.game[this.gameNames[this.$props.game.game.slug]];
  },
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/tournament-form-tokens";
@import "~@/assets/scss/tournament-form-buttons";

/* кнопки дизайн-системы: и в компоненте, и в модалке, которую b-modal уносит в body */
@include tf-buttons;

.ui-gp {
  @include tf-tokens-light;

  border: 1px solid var(--tf-border);
  border-radius: 12px;
  background: var(--tf-bg);
  overflow: hidden;

  & + & { margin-top: 12px; }

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 10px 14px;
    border-bottom: 1px solid var(--tf-border);
    background: var(--tf-soft);
  }

  &__game {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 600;
    color: var(--tf-text);

    i { color: var(--tf-primary); }
  }

  &__id { font-size: 11.5px; color: var(--tf-muted); }

  &__spacer { flex: 1 1 auto; }

  &__wipe:hover {
    border-color: var(--tf-danger);
    color: var(--tf-danger) !important;
    background: var(--tf-danger-soft);
  }

  &__body {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 14px;
    padding: 14px;
  }

  &__field { min-width: 0; }
}

body.dark-only .ui-gp {
  @include tf-tokens-dark;
}

/* поля дублируют стиль из tournament-form.scss: там правило висит на .tf,
   а компонент должен выглядеть так же и сам по себе */
.ui-gp ::v-deep .form-control {
  height: 40px;
  border: 1px solid var(--tf-border);
  border-radius: 10px;
  background: var(--tf-soft);
  color: var(--tf-text);
  font-size: 13.5px;
  box-shadow: none;

  &::placeholder { color: var(--tf-muted); }

  &:focus {
    border-color: var(--tf-primary);
    box-shadow: 0 0 0 3px var(--tf-primary-soft);
  }
}

.tf-label {
  display: block;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: 500;
  color: var(--tf-muted);
}

.tf-badge {
  display: inline-flex;
  align-items: center;
  height: 21px;
  padding: 0 9px;
  border-radius: 999px;
  background: var(--tf-soft-2);
  color: var(--tf-muted);
  font-size: 11px;
  font-weight: 600;

  &--warn { background: var(--tf-warn-soft); color: #b57512; }
}

.tf-spin { animation: ui-gp-spin .8s linear infinite; }

@keyframes ui-gp-spin {
  to { transform: rotate(360deg); }
}
</style>
